// ตัวอ่านกระดาษคำตอบ (OMR) — ตรรกะบริสุทธิ์ ไม่แตะ DOM ทดสอบด้วยภาพสังเคราะห์ได้ (reader.test.js)
//
// ขั้นตอน: หาสี่เหลี่ยมทึบสี่มุม → homography จาก มม. ไปพิกเซล → อ่านแถบรหัสแผ่น (ลองทั้ง 4 ทิศ
// เผื่อสแกนกลับหัว/ตะแคง) → วัดความเข้มของทุกช่องฝนเทียบกับช่องว่างบนแผ่นเดียวกัน
//
// img = { width, height, data } โดย data เป็นความสว่าง 0-255 หนึ่งไบต์ต่อพิกเซล
//
// หลักการที่ห้ามเปลี่ยน
//   * ตำแหน่งทุกอย่างมาจาก sheetLayout.js เท่านั้น
//   * ความเข้มวัด "เทียบกับช่องว่างบนแผ่นเดียวกัน" ไม่ใช่ค่าคงที่ — สแกนเนอร์แต่ละเครื่อง ดินสอ/ปากกา
//     และตัวอักษรจางในวง (ก ข ค ง) ให้ความเข้มพื้นฐานไม่เท่ากัน
//   * ไม่แน่ใจ = บอกครู (uncertain) ไม่ใช่เดา — ฝนจาง ลบไม่สะอาด ฝนสองช่อง ต้องมีคนดูก่อนบันทึก
import { homography } from './homography'
import { MARKERS, PAGE, LAYOUT_VERSION, LAYOUT_VERSIONS, markersFor, codeCells, bubbleLayout, idLayout } from '../sheetLayout'
import { decodeSheetCode, SPARE_STUDY_ID } from '../sheetCode'
import { downscale, findMarkerQuads } from './freeMarkers'

// เกณฑ์ความเข้ม (0 = ขาวเท่ากระดาษ, 1 = ดำสนิท) หลังหักค่าพื้นฐานของช่องว่างแล้ว
// ปรับได้เมื่อได้ภาพสแกนจริง — หน้าสแกนแสดงค่าของทุกช่องให้ดูประกอบ
export const THRESHOLDS = {
  mark: 0.2,     // ถึงเกณฑ์นี้ = ฝน/กากบาทจริง
  faint: 0.06,   // ระหว่าง faint กับ mark = รอยจาง/ลบไม่สะอาด → ให้ครูดู (ต่ำไว้ก่อน: ครูดูเกินไม่กี่ช่องถูกกว่าพลาดรอยลบ)
  gap: 0.15,     // สองช่องที่ถึง mark ต่างกันไม่ถึงเท่านี้ = ฝนสองช่องจริง ไม่ใช่รอยลบ
  codeBit: 0.45, // ช่องแถบรหัสที่พิมพ์ทึบ
}

const ROTATIONS = [0, 1, 2, 3]

// ---- ตัวช่วยภาพ ----

const otsu = (img) => {
  const hist = new Array(256).fill(0)
  const step = Math.max(1, Math.floor(img.data.length / 400000))
  let n = 0
  for (let i = 0; i < img.data.length; i += step) { hist[img.data[i]]++; n++ }
  let sum = 0
  for (let i = 0; i < 256; i++) sum += i * hist[i]
  let sumB = 0; let wB = 0; let best = 0; let t = 128
  for (let i = 0; i < 256; i++) {
    wB += hist[i]
    if (!wB) continue
    const wF = n - wB
    if (!wF) break
    sumB += i * hist[i]
    const mB = sumB / wB
    const mF = (sum - sumB) / wF
    const between = wB * wF * (mB - mF) ** 2
    if (between > best) { best = between; t = i }
  }
  return t
}

// ความสว่างของ "กระดาษ" = เปอร์เซ็นไทล์ 90 — ขอบดำของสแกนเนอร์หรือหมึกไม่ดึงค่านี้ลง
const paperWhite = (img) => {
  const hist = new Array(256).fill(0)
  const step = Math.max(1, Math.floor(img.data.length / 400000))
  let n = 0
  for (let i = 0; i < img.data.length; i += step) { hist[img.data[i]]++; n++ }
  let acc = 0
  for (let v = 255; v >= 0; v--) {
    acc += hist[v]
    if (acc >= n * 0.1) return Math.max(v, 1)
  }
  return 255
}

const lumAt = (img, x, y) => {
  const xi = Math.round(x)
  const yi = Math.round(y)
  if (xi < 0 || yi < 0 || xi >= img.width || yi >= img.height) return 255
  return img.data[yi * img.width + xi]
}

// ---- หาสี่เหลี่ยมมุมกระดาษ ----

// หาก้อนสีเข้มในบริเวณมุมหนึ่งของภาพ แล้วเลือกก้อนที่ "เหมือนสี่เหลี่ยมมุม" และอยู่ใกล้มุมภาพที่สุด
// ขนาดที่คาดคิดจากความกว้างของภาพ (สแกน A4 เต็มหน้า) แต่ยอมให้ต่างได้มากเผื่อสแกนมีขอบหรือถูกย่อ
const findMarkerIn = (img, threshold, region, corner, expected) => {
  const { x0, y0, x1, y1 } = region
  const w = x1 - x0
  const h = y1 - y0
  const seen = new Uint8Array(w * h)
  const dark = (x, y) => img.data[y * img.width + x] < threshold
  const stack = []
  let best = null
  for (let y = y0; y < y1; y++) {
    for (let x = x0; x < x1; x++) {
      const k = (y - y0) * w + (x - x0)
      if (seen[k] || !dark(x, y)) continue
      // flood fill แบบใช้ stack (ไม่ recursive — ก้อนใหญ่จะ stack overflow)
      let count = 0; let sx = 0; let sy = 0
      let minX = x; let maxX = x; let minY = y; let maxY = y
      seen[k] = 1
      stack.push(x, y)
      while (stack.length) {
        const cy = stack.pop()
        const cx = stack.pop()
        count++; sx += cx; sy += cy
        if (cx < minX) minX = cx
        if (cx > maxX) maxX = cx
        if (cy < minY) minY = cy
        if (cy > maxY) maxY = cy
        const nbrs = [cx + 1, cy, cx - 1, cy, cx, cy + 1, cx, cy - 1]
        for (let i = 0; i < 8; i += 2) {
          const nx = nbrs[i]
          const ny = nbrs[i + 1]
          if (nx < x0 || ny < y0 || nx >= x1 || ny >= y1) continue
          const nk = (ny - y0) * w + (nx - x0)
          if (seen[nk] || !dark(nx, ny)) continue
          seen[nk] = 1
          stack.push(nx, ny)
        }
      }
      const bw = maxX - minX + 1
      const bh = maxY - minY + 1
      const aspect = bw / bh
      const fill = count / (bw * bh)
      const size = Math.sqrt(bw * bh)
      // สี่เหลี่ยมที่หมุนไม่เกินราว 20° ยังเต็มกรอบเกิน 0.6
      // ต่ำกว่า 0.7 เท่า — ช่องแถบรหัส (4 มม.) กับช่องที่ฝนเต็ม (~4 มม.) ห้ามถูกนับเป็นสี่เหลี่ยมมุม (7 มม.)
      if (size < expected * 0.7 || size > expected * 1.6) continue
      if (aspect < 0.6 || aspect > 1.66 || fill < 0.6) continue
      const cx = sx / count
      const cy = sy / count
      const d = Math.hypot(cx - corner[0], cy - corner[1])
      if (!best || d < best.d) best = { x: cx, y: cy, size, d }
    }
  }
  return best
}

// คืนศูนย์กลางสี่เหลี่ยมในภาพเรียงตามมุมภาพ ตามเข็ม: [บนซ้าย, บนขวา, ล่างขวา, ล่างซ้าย]
export const findMarkers = (img) => {
  const threshold = otsu(img)
  const { width: W, height: H } = img
  const expected = (MARKERS[0].size * Math.min(W, H)) / Math.min(PAGE.width, PAGE.height)
  const rw = Math.round(W * 0.28)
  const rh = Math.round(H * 0.22)
  const regions = [
    { region: { x0: 0, y0: 0, x1: rw, y1: rh }, corner: [0, 0] },
    { region: { x0: W - rw, y0: 0, x1: W, y1: rh }, corner: [W, 0] },
    { region: { x0: W - rw, y0: H - rh, x1: W, y1: H }, corner: [W, H] },
    { region: { x0: 0, y0: H - rh, x1: rw, y1: H }, corner: [0, H] },
  ]
  const found = regions.map(({ region, corner }) => findMarkerIn(img, threshold, region, corner, expected))
  return found.every(Boolean) ? found : null
}

// ศูนย์กลางสี่เหลี่ยมบนกระดาษ (มม.) เรียงตามเข็มเหมือนกัน
// เส้นกระดาษว่างระหว่างแถวบนกับแถวล่างของแถบรหัส
// (แต่ละแบบกระดาษอยู่คนละความสูง — คำนวณต่อแบบ)
const codeGapY = (cells) => {
  const top = cells[0]
  const bottom = cells[cells.length - 1]
  return (top.y + top.size + bottom.y) / 2
}

// แต่ละแบบกระดาษมีสี่เหลี่ยมมุมคนละตำแหน่ง (แบบ 2 เผื่อที่เย็บเล่มด้านซ้าย) — ลองทุกแบบที่รู้จัก
const cornersFor = (version) => ['tl', 'tr', 'br', 'bl'].map((c) => {
  const m = markersFor(version).find((mk) => mk.corner === c)
  return [m.x + m.size / 2, m.y + m.size / 2]
})
const VERSIONS = LAYOUT_VERSIONS.map((version) => {
  const cells = codeCells(version)
  return { version, corners: cornersFor(version), cells, gapY: codeGapY(cells) }
})

// ---- วัดความเข้ม ----

// ความขาวของกระดาษ "ตรงนั้น" — ภาพจากกล้องมีเงามือถือ/แสงไม่เท่ากันทั้งแผ่น ค่าขาวค่าเดียวทั้งแผ่น
// ทำให้ช่องในเงาดูเหมือนถูกฝน จึงวัดกระดาษรอบๆ แต่ละช่องแทน (เปอร์เซ็นไทล์สูง เผื่อฝนเลอะออกนอกวง)
// แต่ไม่ต่ำกว่าครึ่งหนึ่งของค่าขาวทั้งแผ่น เผื่อรอบช่องถูกขีดเขียนจนดำหมด
const localWhite = (img, toPx, globalWhite, points) => {
  const values = points.map(([x, y]) => {
    const [px, py] = toPx(x, y)
    return lumAt(img, px, py)
  }).sort((a, b) => a - b)
  const high = values[Math.floor((values.length - 1) * 0.9)]
  return Math.max(high, globalWhite * 0.5, 1)
}

const ringPoints = (cx, cy, r0, r1) => {
  const pts = []
  for (const r of [r0, (r0 + r1) / 2, r1]) {
    for (let k = 0; k < 16; k++) {
      const a = (k / 16) * 2 * Math.PI
      pts.push([cx + r * Math.cos(a), cy + r * Math.sin(a)])
    }
  }
  return pts
}

// ความเข้มเฉลี่ยในวงกลม (มม.) — สุ่มจุดเป็นตารางภายในรัศมี ไม่แตะเส้นขอบวงที่พิมพ์ไว้
// white วัดจากวงแหวนกระดาษนอกเส้นขอบ (r_outer + 0.5..0.8 มม.) ซึ่งยังไม่ถึงช่องข้างๆ หรือเลขข้อ
const circleDarkness = (img, toPx, globalWhite, cx, cy, r, rOuter) => {
  const white = localWhite(img, toPx, globalWhite, ringPoints(cx, cy, rOuter + 0.5, rOuter + 0.8))
  let sum = 0; let n = 0
  const step = r / 4
  for (let dy = -r; dy <= r + 1e-9; dy += step) {
    for (let dx = -r; dx <= r + 1e-9; dx += step) {
      if (dx * dx + dy * dy > r * r) continue
      const [x, y] = toPx(cx + dx, cy + dy)
      sum += Math.min(1, lumAt(img, x, y) / white)
      n++
    }
  }
  return 1 - sum / n
}

// white ของช่องแถบรหัสวัดจากเส้นกระดาษว่างระหว่างสองแถวของแถบ ตรงคอลัมน์เดียวกัน
const squareDarkness = (img, toPx, globalWhite, x0, y0, size, gapY) => {
  const mid = x0 + size / 2
  const white = localWhite(img, toPx, globalWhite, [-1, -0.5, 0, 0.5, 1].map((dx) => [mid + dx, gapY]))
  let sum = 0; let n = 0
  const inset = size * 0.2
  const step = (size - 2 * inset) / 4
  for (let i = 0; i <= 4; i++) {
    for (let j = 0; j <= 4; j++) {
      const [x, y] = toPx(x0 + inset + j * step, y0 + inset + i * step)
      sum += Math.min(1, lumAt(img, x, y) / white)
      n++
    }
  }
  return 1 - sum / n
}

const percentile = (values, p) => {
  if (!values.length) return 0
  const s = [...values].sort((a, b) => a - b)
  return s[Math.min(s.length - 1, Math.floor(p * (s.length - 1)))]
}

// ตัดสินคำตอบของข้อหนึ่งจากค่าความเข้มที่หักพื้นแล้วของแต่ละช่อง
export const decideRow = (options, deltas, t = THRESHOLDS) => {
  const ranked = options.map((o, i) => ({ o, d: deltas[i] })).sort((a, b) => b.d - a.d)
  const [top, second] = ranked
  const marked = ranked.filter((r) => r.d >= t.mark)
  if (!marked.length) {
    return { answer: '', uncertain: top.d >= t.faint }
  }
  if (marked.length === 1) {
    return { answer: top.o, uncertain: !!second && second.d >= t.faint }
  }
  // ถึงเกณฑ์หลายช่อง: ช่องหนึ่งเข้มกว่าชัดเจน = น่าจะลบอีกช่องไม่สะอาด (ยังให้ครูดู)
  if (top.d - second.d >= t.gap) return { answer: top.o, uncertain: true }
  return { answer: '#', uncertain: true }
}

// อ่านกระดาษหนึ่งแผ่น
//   exam = { exam_id, exam_choices }, questions = [{ eq_no, eq_type }] ของข้อสอบชุดนี้
// คืน { ok, error?, studyId, rotation, answers: { eq_no: code }, uncertain: [eq_no], rows, markers }
//   กระดาษสำรอง: ok = true, spare = true, studyId = null และ
//   idNumber = เลขประจำตัวที่ฝน (null ถ้าฝนไม่ครบ/ฝนสองช่อง/จาง), idRows = ช่องฝนเลขสำหรับวาดทับภาพ
//   — ผู้เรียกต้องจับคู่เลขกับรายชื่อเอง และให้ครูยืนยันทุกแผ่น (เลขที่ฝนไม่มี CRC แบบแถบรหัส)
//   opts.markers = สี่เหลี่ยมมุมที่หามาแล้ว (กล้องใช้ findMarkersFree บนภาพย่อ) — ไม่ส่ง = หาเองแบบภาพสแกน
export const readSheet = (img, exam, questions, t = THRESHOLDS, opts = {}) => {
  const markers = opts.markers || findMarkers(img)
  if (!markers) return { ok: false, error: 'ไม่พบสี่เหลี่ยมดำครบ 4 มุม (กระดาษเอียงมาก ถูกตัดขอบ หรือไม่ใช่กระดาษคำตอบของระบบนี้)' }

  const white = paperWhite(img)

  // ลองทุกแบบกระดาษ x ทั้ง 4 ทิศ — คู่ที่ถอดแถบรหัสผ่าน CRC คือคู่ที่ถูก
  // (แบบผิดทำให้จุดที่อ่านแถบเลื่อนไปหลายมิลลิเมตร CRC ไม่ผ่าน จึงไม่มีทางได้แบบผิดมาเงียบๆ)
  let toPx = null
  let code = null
  let rotation = 0
  let version = LAYOUT_VERSION
  search:
  for (const v of VERSIONS) {
    for (const k of ROTATIONS) {
      const dst = v.corners.map((_, i) => {
        const m = markers[(i + k) % 4]
        return [m.x, m.y]
      })
      const map = homography(v.corners, dst)
      if (!map) continue
      const bits = v.cells.map((c) => (squareDarkness(img, map, white, c.x, c.y, c.size, v.gapY) >= t.codeBit ? 1 : 0))
      const decoded = decodeSheetCode(bits)
      if (decoded) {
        toPx = map
        code = decoded
        rotation = k * 90
        version = v.version
        break search
      }
    }
  }
  if (code && code.examId !== exam.exam_id) {
    return { ok: false, error: `เป็นกระดาษของข้อสอบชุดอื่น (รหัส E${code.examId})`, markers, wrongExam: true, studyId: code.studyId }
  }
  // อ่านรหัสไม่ได้: ยังอ่านช่องฝนต่อโดยถือว่าวางตั้งตรง (กรณีส่วนใหญ่) ครูเลือกนักเรียนเองแล้วไม่ต้องกรอกคำตอบใหม่
  // ถ้าจริงๆ แผ่นกลับหัว คำตอบจะผิดทั้งแผ่น ซึ่งครูเห็นได้จากภาพที่ระบายทับไว้
  // (ไม่รู้แบบกระดาษด้วย — ใช้แบบปัจจุบัน ซึ่งเป็นแผ่นส่วนใหญ่)
  if (!code) toPx = homography(cornersFor(LAYOUT_VERSION), markers.map((m) => [m.x, m.y]))
  if (!toPx) return { ok: false, error: 'ตำแหน่งสี่เหลี่ยมมุมผิดรูป อ่านกระดาษไม่ได้', markers }

  const spare = !!code && code.studyId === SPARE_STUDY_ID
  let layout
  try {
    layout = bubbleLayout(questions, exam.exam_choices, spare, version)
  } catch (err) {
    return { ok: false, error: err.message, markers }
  }
  const darkness = (b) => circleDarkness(img, toPx, white, b.cx, b.cy, b.r * 0.62, b.r)
  const raw = layout.map((row) => row.bubbles.map(darkness))
  const idGrid = spare ? idLayout(version).digits : []
  const idRaw = idGrid.map((d) => d.bubbles.map(darkness))
  // ค่าพื้นของช่องว่าง = เปอร์เซ็นไทล์ 25 ของทุกช่องบนแผ่น (รวมช่องฝนเลขประจำตัวของกระดาษสำรอง)
  // (ข้อหนึ่งฝนได้ช่องเดียว ช่องว่างจึงมีอย่างน้อยครึ่งหนึ่งเสมอแม้ข้อสอบเป็นใช่/ไม่ใช่ทั้งหมด)
  const base = percentile([...raw.flat(), ...idRaw.flat()], 0.25)

  const answers = {}
  const uncertain = []
  const rows = layout.map((row, i) => {
    const deltas = raw[i].map((d) => Math.round((d - base) * 1000) / 1000)
    const decision = decideRow(row.bubbles.map((b) => b.value), deltas, t)
    answers[row.eq_no] = decision.answer
    if (decision.uncertain) uncertain.push(row.eq_no)
    return {
      eq_no: row.eq_no,
      ...decision,
      bubbles: row.bubbles.map((b, j) => {
        const [x, y] = toPx(b.cx, b.cy)
        const [ex, ey] = toPx(b.cx + b.r, b.cy)
        return { value: b.value, x, y, r: Math.hypot(ex - x, ey - y), delta: deltas[j] }
      }),
    }
  })

  if (!code) {
    return {
      ok: false, noCode: true, error: 'อ่านรหัสแผ่นไม่ได้ (แถบรหัสเปื้อน/ถูกขีด หรือสแกนไม่ชัด) — เลือกนักเรียนเอง',
      rotation: 0, answers, uncertain, rows, markers, base,
    }
  }
  if (spare) {
    // เลขประจำตัวทีละหลัก ตัดสินด้วยเกณฑ์ชุดเดียวกับข้อคำตอบ — หลักไหนว่าง/ฝนสองช่อง/ไม่แน่ใจ = ไม่เดาเลข
    const values = idGrid[0]?.bubbles.map((b) => b.value) || []
    const idRows = idGrid.map((d, i) => {
      const deltas = idRaw[i].map((v) => Math.round((v - base) * 1000) / 1000)
      const decision = decideRow(values, deltas, t)
      return {
        digit: d.index,
        ...decision,
        bubbles: d.bubbles.map((b, j) => {
          const [x, y] = toPx(b.cx, b.cy)
          const [ex, ey] = toPx(b.cx + b.r, b.cy)
          return { value: b.value, x, y, r: Math.hypot(ex - x, ey - y), delta: deltas[j] }
        }),
      }
    })
    const clean = idRows.every((r) => /^\d$/.test(r.answer) && !r.uncertain)
    return {
      ok: true, spare: true, studyId: null, rotation, version, answers, uncertain, rows, markers, base,
      idNumber: clean ? idRows.map((r) => r.answer).join('') : null,
      idRead: idRows.map((r) => (/^\d$/.test(r.answer) ? r.answer : r.answer === '#' ? '#' : '_')).join(''),
      idRows,
    }
  }
  return { ok: true, studyId: code.studyId, rotation, version, answers, uncertain, rows, markers, base }
}

// อ่านภาพหนึ่งเฟรมจากกล้อง — หามุมบนภาพย่อ (เร็ว) แล้วอ่านช่องฝนบนภาพเต็ม (ละเอียด)
// ลอง quad ที่เป็นไปได้ทีละตัวจนแถบรหัสถอดผ่าน; แผ่นของข้อสอบอื่นก็ถือว่า "เจอแล้ว" (บอกครูได้เลย)
export const readCameraFrame = (img, exam, questions, t = THRESHOLDS) => {
  const { img: small, k } = downscale(img)
  const quads = findMarkerQuads(small)
  if (!quads.length) return { ok: false, found: false, error: 'ยังไม่เห็นสี่เหลี่ยมดำครบ 4 มุม' }
  let last = null
  for (const quad of quads) {
    const markers = quad.map((m) => ({ x: m.x * k, y: m.y * k, size: m.size * k }))
    const res = readSheet(img, exam, questions, t, { markers })
    if (res.ok || res.wrongExam) return { ...res, found: true }   // รวมกระดาษสำรอง (ok + spare)
    last = res
  }
  // เห็นมุมแต่ถอดรหัสไม่ได้ — ภาพยังเบลอ/เอียงมาก หรือแถบรหัสถูกบัง ให้ถือนิ่งๆ แล้วลองเฟรมถัดไป
  return { ...last, ok: false, found: true, noCode: true, error: 'เห็นกระดาษแล้วแต่อ่านรหัสแผ่นไม่ได้ — ถือนิ่งๆ ให้แถบรหัสชัด' }
}
